/**CORE LIBRARIES IMPORT */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**COMPONENTS IMPORT */
import FeaturedEvent from "../Components/FeaturedEvent";
import AboutUs from "../Components/AboutUs";
import MenuPreview from "../Components/MenuPreview";
import PrivateEvents from "../Components/PrivateEvents";
import BookingCta from "../Components/BookingCta";
import ContactSection from "../Components/ContactSection";
import useSiteSettings from "../hooks/useSiteSettings";

/**DATA / HOOKS IMPORT */
import { featuredEvent, eventDateParts } from "../data/events";
import { BOOK_TABLE_PATH } from "../data/navigation";
import usePageTitle from "../hooks/usePageTitle";

const MotionLink = motion.create(Link);

const HeroImage = () => {
  const hero = useSiteSettings().media?.hero;
  if (!hero) return null;
  return <img className="hero-image" src={hero.src} alt={hero.alt} style={{ objectPosition: hero.position }} fetchpriority="high" decoding="async" />;
};


const Home=()=>{
    usePageTitle();

    const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

    const eventDate = featuredEvent && eventDateParts(featuredEvent.date);

    return(
        <main>
       {/**HERO SECTION */}
       <section className="hero" id="hero" aria-label="The Grey Wolf">
        <motion.div
  className="light-sweep"
  aria-hidden="true"
  animate={{
    x: ["-100%", "100%"]
  }}
  transition={{
    repeat: Infinity,
    duration: 6,
    ease: "linear"
  }}
/>

  {/* Hero photo from the portal (Website Media); without one the original gradient shows. */}
  <HeroImage />

  <div className="hero-overlay"></div>

  <div className="hero-container">

    {/* LEFT TEXT */}
    <motion.div
  className="hero-left"
  initial={{ opacity: 0, x: -60 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{
    duration: 1,
    delay: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }}
>
  <motion.p
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 0.8, duration: 1 }}
  >
    Wood-fired pizza &amp; grill<br />
    inside Lyster's Pub,<br />
    Harold's Cross, Dublin.<br />
    Great food. Good times.
  </motion.p>
</motion.div>

    {/* RIGHT CONTENT */}
    <motion.div
  className="hero-right"
  initial="hidden"
  animate="visible"
  variants={{
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  }}
>
   <motion.h1 className="hero-heading" aria-label="The Grey Wolf">
  <motion.span className="outline" variants={fadeUp} aria-hidden="true">
    Th
  </motion.span>
  <motion.span className="solid" variants={fadeUp} aria-hidden="true">
    e Grey
  </motion.span>
  <br />

  <motion.span className="outline" variants={fadeUp} aria-hidden="true">
    W
  </motion.span>
  <motion.span className="solid" variants={fadeUp} aria-hidden="true">
    olf
  </motion.span>
</motion.h1>

      {featuredEvent && (
        <motion.p className="hero-event-tag" variants={fadeUp}>
          <Link to={`/events/${featuredEvent.slug}`}>
            <span className="hero-event-tag-label">Featured</span>
            {featuredEvent.title} · {eventDate.day} {eventDate.month} {eventDate.year}
          </Link>
        </motion.p>
      )}

      <div className="hero-buttons">
        <MotionLink
          to="/menu"
          className="hero-btn"
          whileHover={{ y: -6, scale: 1.05 }}
          whileTap={{ scale: 0.96 }}
        >
          View Menu
        </MotionLink>

        <MotionLink
          to={BOOK_TABLE_PATH}
          className="hero-btn"
          whileHover={{ y: -6, scale: 1.05 }}
          whileTap={{ scale: 0.96 }}
        >
          Book a Table
        </MotionLink>
</div>
    </motion.div>

  </div>
</section>


{/**FEATURED JOINT EVENT */}
<FeaturedEvent event={featuredEvent} />

{/**DISCOVER OUR VENUE (template About section + exterior photo) */}
<AboutUs />

{/**MENU PREVIEW (template DJ card grid) */}
<MenuPreview />

{/**PRIVATE EVENTS & CATERING */}
<PrivateEvents />

{/**BOOKING CTA */}
<BookingCta />

{/**FIND US */}
<ContactSection />
        </main>
    )
}

export default Home;
